import React, { createContext, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coreApi, setCSRFToken } from '../api/client';

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
}

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  mode?: string;
}

export interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  permissions: Record<string, boolean>;
  enabledSuites: string[];
  enabledModules: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, pass: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
  hasModule: (moduleId: string) => boolean;
}

interface MeResponse {
  user_id: string;
  email: string;
  permissions: Record<string, boolean>;
  tenant?: Tenant;
  enabled_suites?: string[];
  enabled_modules?: string[];
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export function hasPermissionCheck(granted: Record<string, boolean>, required: string): boolean {
  if (!granted || !required) return false;
  if (granted['*']) return true;
  if (granted[required]) return true;

  const parts = required.split('.');
  if (parts.length >= 2) {
    const wildcard = `${parts[0]}.*`;
    if (granted[wildcard]) return true;
  }
  return false;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  const { data: meData, isLoading } = useQuery<MeResponse | null>({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await coreApi.get<MeResponse>('/auth/me');
      } catch (err: unknown) {
        return null;
      }
    },
    retry: false,
    staleTime: 60000,
  });

  const loginMutation = useMutation({
    mutationFn: async ({ email, pass }: { email: string; pass: string }) => {
      const resp = await coreApi.post<{ user_id: string; email: string; csrf_token: string }>('/auth/login', {
        email,
        password: pass,
      });
      if (resp.csrf_token) {
        setCSRFToken(resp.csrf_token);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await coreApi.post('/auth/logout');
      setCSRFToken(null);
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
      window.location.href = '/login';
    },
  });

  const isAuthenticated = !!meData?.user_id;

  const user: User | null = meData
    ? {
        id: meData.user_id,
        email: meData.email,
        name: meData.email.split('@')[0],
      }
    : null;

  const permissions = meData?.permissions || {};
  const enabledSuites = meData?.enabled_suites || [
    'core',
    'operations',
    'sales',
    'crm',
    'finance',
    'projects',
    'inventory',
    'helpdesk',
    'people',
    'support',
    'marketing',
    'data',
    'delivery',
    'settings',
  ];
  const tenant = meData?.tenant || {
    id: 'default',
    slug: 'acme',
    name: 'Acme Corporation',
  };

  const enabledModules = meData?.enabled_modules || [];
  const hasModule = (moduleId: string): boolean => {
    if (moduleId === 'core.basic' || moduleId === 'core.email' || moduleId === 'core.calendar') return true;
    if (permissions?.['*']) return true;
    if (!enabledModules || enabledModules.length === 0) return true;
    return enabledModules.includes(moduleId);
  };

  const hasPermission = (perm: string) => hasPermissionCheck(permissions, perm);

  const value: AuthState = {
    user,
    tenant,
    permissions,
    enabledSuites,
    enabledModules,
    isAuthenticated,
    isLoading,
    login: async (email, pass) => {
      await loginMutation.mutateAsync({ email, pass });
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    hasPermission,
    hasModule,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
